import {Injectable} from '@angular/core';
import {EnvironmentService} from '@/_services/environment.service';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs';
import {Utils} from '@/classes/utils';
import {GLOBALS} from '@/_services/globals.service';
import {AppData} from '@/_model/app-data';
import {PersonData} from '@/_model/person-data';
import {UserData} from '@/_model/user-data';

@Injectable({
  providedIn: 'root'
})
export class BackendService {

  token: string;
  apiToken: string;
  remainingUsers: number;
  serviceAvailable = true;

  constructor(public env: EnvironmentService,
              private http: HttpClient) {
  }

  get apiUrl(): string {
    return `${this.env.apiUrl}/${this.apiToken}`;
  }

  may(_check: string): boolean {
    return true;
  }

  /**
   * Register a new user and save the data for the person
   * @param username username
   * @param pwd      password
   * @param type     usertype
   * @param data     data for saving
   * @param onDone   called when done
   * @param onError  called on error during processing
   */
  register(username: string, pwd: string, type: number, data: AppData, onDone: (data: AppData) => void, onError?: (error: any) => void): void {
    Utils.hash(pwd).then((hash: string) => {
      this.query({data: data.forBackend}, `register|${username}|${hash}|${type}`)
        .subscribe({
          next: response => {
            // save token for future requests without login-data
            this.token = response.u.token;
            this.apiToken = response.u.apiToken;
            // extract AppData from response and save the usertype
            const ret = new AppData();
            ret.fillFromBackend(response.d);
            ret.usertype = response.u.type;
            onDone?.(ret);
          },
          // request to login was rejected
          error: error => {
            onError?.(error);
          }
        })
    });
  }

  /**
   * Login to the backend.
   * @param username username
   * @param pwd      password
   * @param onDone   called when done
   * @param onError  called on error during processing
   */
  login(username: string, pwd: string, onDone: (data: any) => void, onError?: (error: any) => void): void {
    Utils.hash(pwd).then((hash: string) => {
      this.query({}, `auth|${username}|${hash}`)
        .subscribe({
          next: response => {
            // save token for future requests without login-data
            this.token = response.u.token;
            this.apiToken = response.u.apiToken;
            this.loadAppData((data) => {
              onDone?.({data: data, perm: response.u.permissions, type: response.u.type});
            });
          },
          // request to login was rejected
          error: error => {
            onError?.(error);
          }
        });
    });
  }

  changePassword(pwdOld: string, pwdNew: string, onDone: () => void, onError?: (error: any) => void): void {
    Utils.hash(pwdOld).then((hashOld: string) => {
      Utils.hash(pwdNew).then((hashNew: string) => {
        this.query({cmd: 'changePwd', po: hashOld, pn: hashNew}, this.token)
          .subscribe({
            next: _response => {
              onDone?.();
            },
            error: error => {
              if (error.error.ec === 404) {
                error = $localize`Old password is wrong`;
              } else {
                error = error.error.em;
              }
              onError?.(error);
            }
          });
      });
    });
  }

  getPersonList(type: number, onDone: (data: PersonData[]) => void, onError?: (error: any) => void): void {
    this.query({cmd: 'loadPersonList', type: type}, this.token)
      .subscribe({
        next: response => {
          const ret: PersonData[] = [];
          for (const src of response) {
            const dst = new AppData();
            dst.fillFromBackend(src.d.data);
            dst.person.fkUser = src.u;
            ret.push(dst.person);
          }
          onDone?.(ret);
        },
        // request to login was rejected
        error: error => {
          onError?.(error);
        }
      });
  }

  loginByToken(onDone: (data: any) => void, onError?: (error: any) => void): void {
    this.query({cmd: 'loadAppData', id: 1}, this.token)
      .subscribe({
        next: response => {
          this.remainingUsers = response.ru;
          this.apiToken = response.at;
          onDone?.(response);
        },
        // request to login was rejected
        error: error => {
          this.remainingUsers = +(error?.error?.r);
          this.serviceAvailable = error?.error?.r != null;
          onError?.(error);
        }
      })
  }

  loadUserList(onDone: (data: UserData[]) => void, onError?: (error: any) => void): void {
    this.query({cmd: 'loadUserList'})
      .subscribe({
        next: (response: any) => {
          const ret: UserData[] = [];
          for (const src of response) {
            ret.push(new UserData(src));
          }
          onDone(ret);
        },
        error: error => {
          console.error(error);
          onError?.(error);
        }
      });
  }

  loadAppData(onDone: (data: AppData) => void, onError?: (error: any) => void): void {
    this.query({cmd: 'loadAppData', id: 1})
      .subscribe({
        next: (response: any) => {
          const ret = new AppData();
          ret.fillFromBackend(response.data);
          ret.permissions = response.perm.split(',').map((entry: string) => +entry);
          onDone?.(ret);
        },
        error: error => {
          console.error(error);
          onError?.(error);
        }
      });
  }

  deleteUser(data: UserData, onError?: (error: any) => void): void {
    this.query({cmd: 'deleteUser', id: data.id, data: data.forBackend})
      .subscribe({
        next: (_response: any) => {
        },
        error: error => {
          console.error(error);
          onError?.(error);
        }
      });
  }

  saveUser(data: UserData, onError?: (error: any) => void): void {
    this.query({cmd: 'saveUser', id: data.id, data: data.forBackend})
      .subscribe({
        next: (_response: any) => {
        },
        error: error => {
          console.error(error);
          onError?.(error);
        }
      });
  }

  saveAppDataSilent(onDone?: (data: AppData) => void, onError?: (error: any) => void): void {
    this.saveAppData(GLOBALS.appData,
      (data) => {
        GLOBALS.appData.fillFromJson(data.asJson);
        GLOBALS.appData.usertype = data.usertype;
        GLOBALS.currentUserType = GLOBALS.usertypeList[0];
        GLOBALS.saveSharedData();
        onDone?.(data);
      }, onError);
  }

  saveAppData(data: AppData, onDone: (data: AppData) => void, onError?: (error: any) => void): void {
    this.query({cmd: 'saveAppData', id: data.id, data: data.forBackend, usertype: data.usertype})
      .subscribe({
        next: (response: any) => {
          const ret = new AppData();
          ret.fillFromBackend(response.data);
          ret.usertype = response.usertype;
          onDone(ret);
        },
        error: error => {
          console.error(error);
          onError?.(error);
        }
      });
  }

  logout(onDone: () => void, onError?: (error: any) => void): void {
    this.query({cmd: 'logout'})
      .subscribe({
        next: (_response: any) => {
          this.token = null;
          this.apiToken = null;
          GLOBALS.appData = null;
          GLOBALS.currentUserType = null;
          GLOBALS.saveSharedData();
          onDone?.();
        },
        error: error => {
          console.error(error);
          onError?.(error);
        }
      });
  }

  private query(body: any, token = this.token): Observable<any> {
    return this.http.post(`${this.env.backendUrl}`, body,
      {headers: this.header(token)});
  }

  private header(token: string): HttpHeaders {
    return new HttpHeaders({
      'Authorization': token ?? '-',
      'Content-Type': 'application/json'
    });
  }
}
